/**
 * StocksWorkspace - Interface dédiée à la gestion des stocks
 *
 * Accessible aux vendeurs ayant la workstation STOCKS assignée.
 * Fonctionnalités :
 * - Mise en rayon (déplacer stock réserve → rayon)
 * - Fiche produit détaillée (lecture seule)
 * - Impression d'étiquettes ZEBRA
 *
 * Interface optimisée pour tablette (iPad).
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSmartBack } from '../hooks/useSmartBack';
import { usePOSStore, WORKSTATION_CODES } from '../hooks/usePOSStore';
import { useTenant } from '../contexts/TenantContext';
import api from '../services/api';
import { posApi } from '../api/pos.api';
import { formatQty } from '../utils/pos.utils';
import { PrintShelfLabelsModal } from '../components/labels/PrintShelfLabelsModal';
import { useBarcodeScan } from '../hooks/useBarcodeScan';
import type { ProductLot } from '../types/lots.types';
import DLCInput from '../components/ui/DLCInput';
import ProductCountModal from '../components/ProductCountModal';
import BilanSeriesModal from '../components/pos/BilanSeriesModal';
import RemiseLotsModal from '../components/remise/RemiseLotsModal';
import { searchProducts } from '../utils/productSearch';

interface Product {
  id: string;
  code: string;
  name: string;
  barcode?: string;
  category_id?: string;
  category_name?: string;
  stock_backroom: number;
  stock_display: number;
  unit: string;
  supplier_name?: string;
  price_cents?: number;
  brand?: string;
  description?: string;
  updated_at: string;
  // Info du lot principal (FEFO)
  primary_lot_id?: string;
  primary_lot_expiry_date?: string | null;
}

interface Category {
  id: string;
  name: string;
  color?: string;
}

export default function StocksWorkspace() {
  const navigate = useNavigate();
  // « ← Accueil » : /accueil ou /welcome selon d'où l'on vient ; /welcome si ouverte directement.
  const goBack = useSmartBack('/welcome');
  const { tenant } = useTenant();
  const vendorName = usePOSStore((state) => state.currentVendorName);
  const vendorRole = usePOSStore((state) => state.currentVendorRole);
  const hasWorkstationAccess = usePOSStore((state) => state.hasWorkstationAccess);

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [movingProduct, setMovingProduct] = useState<string | null>(null);
  const [moveQuantities, setMoveQuantities] = useState<Record<string, number>>({});

  // Modal fiche produit
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductDetail, setShowProductDetail] = useState(false);

  // Lots du produit sélectionné (traçabilité AFSCA)
  const [productLots, setProductLots] = useState<ProductLot[]>([]);
  const [lotsLoading, setLotsLoading] = useState(false);

  // Comptage d'un produit (mini-inventaire : ajustement tracé lot par lot)
  const [countProduct, setCountProduct] = useState<Product | null>(null);
  const [seriesProduct, setSeriesProduct] = useState<Product | null>(null);
  // Remise anti-gaspi par lot, posée depuis la fiche (porte d'entrée du quotidien).
  const [remiseProduct, setRemiseProduct] = useState<Product | null>(null);

  // Modal impression étiquette
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [productForLabel, setProductForLabel] = useState<Product | null>(null);

  // Vérifier l'accès
  const canAccessStocks = hasWorkstationAccess(WORKSTATION_CODES.STOCKS);

  // Scan global code-barres → remplit le champ de recherche
  useBarcodeScan({
    onScan: (barcode) => {
      setSearchTerm(barcode);
    },
  });

  useEffect(() => {
    if (tenant?.id) {
      loadData();
    }
  }, [tenant?.id]);

  // Un ajustement fait ailleurs (fiche produit, autre poste) ne remonte pas tout
  // seul jusqu'ici : la page ne chargeait qu'à l'ouverture, et il fallait
  // recharger à la main pour voir le bon stock. On redemande donc les chiffres
  // dès que l'écran redevient actif — c'est le moment exact où on les regarde.
  useEffect(() => {
    if (!tenant?.id) return;
    const rafraichir = () => {
      if (document.visibilityState === 'visible') loadData();
    };
    window.addEventListener('focus', rafraichir);
    document.addEventListener('visibilitychange', rafraichir);
    return () => {
      window.removeEventListener('focus', rafraichir);
      document.removeEventListener('visibilitychange', rafraichir);
    };
  }, [tenant?.id]);

  // Charger les lots quand on ouvre la fiche produit
  useEffect(() => {
    if (showProductDetail && selectedProduct) {
      setLotsLoading(true);
      posApi.getProductLots(selectedProduct.id)
        .then(setProductLots)
        .catch((err) => console.error('Erreur chargement lots:', err))
        .finally(() => setLotsLoading(false));
    } else {
      setProductLots([]);
    }
  }, [showProductDetail, selectedProduct]);

  const loadData = async () => {
    if (!tenant?.id) return;

    try {
      setLoading(true);
      // Charger les produits avec stock (via lots) ET tous les produits du catalogue
      const [stockData, productsResponse, categoriesData] = await Promise.all([
        api.getAllProductsWithStock(tenant.id).catch(() => []),
        api.getProducts({ limit: 5000 }),
        api.getCategories().catch(() => []),
      ]);

      // Extraire les produits de la réponse
      const allProductsData = productsResponse.data || productsResponse || [];

      // Créer un map des stocks depuis les lots (réserve ET rayon)
      const stockMap = new Map<string, {
        stock_backroom: number;
        stock_display: number;
        unit: string;
        category_name?: string;
        primary_lot_id?: string;
        primary_lot_expiry_date?: string | null;
      }>();
      (stockData || []).forEach((item: any) => {
        if (item.id) {
          stockMap.set(item.id, {
            stock_backroom: item.stock_backroom || 0,
            stock_display: item.stock_display || 0,
            unit: item.unit || 'pcs',
            category_name: item.category_name,
            primary_lot_id: item.primary_lot_id,
            primary_lot_expiry_date: item.primary_lot_expiry_date,
          });
        }
      });

      // Mapper tous les produits avec leurs stocks réels
      // Pour les produits sans lots (legacy), utiliser stock_quantity comme fallback
      const mappedProducts: Product[] = allProductsData.map((p: any) => {
        const stockInfo = stockMap.get(p.id);

        // Fallback: si pas de données de lot, utiliser les champs dépréciés du produit
        // Les produits legacy ont stock_quantity sur le Product directement
        const legacyStockQuantity = p.stock_quantity || 0;
        const legacyStockBackroom = p.stock_backroom || 0;
        const legacyStockDisplay = p.stock_display || 0;

        // Si on a des infos de lot, les utiliser, sinon fallback sur legacy
        const hasLotInfo = stockInfo !== undefined;

        return {
          id: p.id,
          code: p.code || p.sku || '',
          name: p.name,
          barcode: p.barcode,
          category_id: p.category_id,
          category_name: stockInfo?.category_name || p.category?.name || categoriesData.find((c: any) => c.id === p.category_id)?.name,
          // Priorité: stocks des lots > champs legacy du produit
          stock_backroom: hasLotInfo ? stockInfo.stock_backroom : legacyStockBackroom,
          stock_display: hasLotInfo ? stockInfo.stock_display : (legacyStockDisplay || legacyStockQuantity),
          unit: stockInfo?.unit || p.unit || 'pcs',
          supplier_name: p.supplier_name,
          price_cents: p.price_cents,
          brand: p.brand,
          description: p.description,
          updated_at: p.updated_at,
          // Info du lot principal (FEFO)
          primary_lot_id: stockInfo?.primary_lot_id,
          primary_lot_expiry_date: stockInfo?.primary_lot_expiry_date,
        };
      });

      setProducts(mappedProducts);

      // Initialiser les quantités pour ceux qui ont du stock en réserve
      const initialQuantities: Record<string, number> = {};
      mappedProducts.forEach((p: Product) => {
        if (p.stock_backroom > 0) {
          initialQuantities[p.id] = Math.min(p.stock_backroom, 1);
        }
      });
      setMoveQuantities(initialQuantities);
    } catch (err: any) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveToDisplay = async (productId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!tenant?.id) return;

    const quantity = moveQuantities[productId] || 0;
    if (quantity <= 0) {
      alert('La quantité doit être supérieure à 0');
      return;
    }

    try {
      setMovingProduct(productId);
      await api.moveToDisplay(tenant.id, productId, quantity);
      alert(`${quantity} unités déplacées vers le rayon`);
      loadData();
    } catch (err: any) {
      console.error('Failed to move to display:', err);
      alert(err.response?.data?.message || 'Échec du déplacement');
    } finally {
      setMovingProduct(null);
    }
  };

  const updateQuantity = (productId: string, value: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const maxQuantity = product.stock_backroom;
    const newQuantity = Math.max(0, Math.min(value, maxQuantity));

    setMoveQuantities(prev => ({
      ...prev,
      [productId]: newQuantity,
    }));
  };

  // Mettre à jour la DLC d'un lot directement depuis la liste
  const handleUpdateDLC = async (productId: string, lotId: string, newDate: string | null, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await posApi.updateLot(lotId, { expiry_date: newDate });
      // Mettre à jour localement
      setProducts(prev => prev.map(p =>
        p.id === productId ? { ...p, primary_lot_expiry_date: newDate } : p
      ));
    } catch (err) {
      console.error('Erreur mise à jour DLC:', err);
      alert('Erreur lors de la mise à jour de la DLC');
    }
  };

  const openProductDetail = (product: Product) => {
    setSelectedProduct(product);
    setShowProductDetail(true);
  };

  const openLabelDialog = (product: Product, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setProductForLabel(product);
    setShowLabelDialog(true);
  };

  // Extraire les catégories uniques des produits
  const productCategories = useMemo(() => {
    const catMap = new Map<string, Category>();
    products.forEach(p => {
      if (p.category_id && p.category_name) {
        catMap.set(p.category_id, {
          id: p.category_id,
          name: p.category_name
        });
      }
    });
    return Array.from(catMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [products]);

  // Filtrer les produits — mêmes règles de recherche que la boutique :
  // accents et casse ignorés, mots dans n'importe quel ordre, singulier ≈
  // pluriel, une faute tolérée. Avant, c'était un `includes` de la phrase
  // entière : « carotte » ne trouvait pas « Carotte », « pdt agria » ne
  // trouvait rien, et il fallait taper sans faute (signalé le 03/09/2026).
  const filteredProducts = useMemo(() => {
    const dansLaCategorie = selectedCategory
      ? products.filter(p => p.category_id === selectedCategory)
      : products;
    if (!searchTerm.trim()) return dansLaCategorie;
    return searchProducts(dansLaCategorie, searchTerm, (p) => ({
      name: p.name,
      category: p.category_name || undefined,
      other: [p.code, p.barcode].filter(Boolean).join(' '),
    }));
  }, [products, searchTerm, selectedCategory]);

  // Grouper par catégorie pour l'affichage
  const productsByCategory = useMemo(() => {
    const grouped: Record<string, Product[]> = {};

    filteredProducts.forEach(p => {
      const catKey = p.category_name || 'Sans catégorie';
      if (!grouped[catKey]) {
        grouped[catKey] = [];
      }
      grouped[catKey].push(p);
    });

    // Trier les catégories alphabétiquement
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredProducts]);

  const formatPrice = (cents?: number) => {
    if (!cents) return '-';
    return `${(cents / 100).toFixed(2)} €`;
  };

  // Si pas d'accès, afficher message
  if (!canAccessStocks) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <span className="text-6xl mb-4 block">🚫</span>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Accès non autorisé</h2>
          <p className="text-gray-600 mb-6">
            Vous n'avez pas accès à la gestion des stocks.
            Contactez votre administrateur pour obtenir les permissions nécessaires.
          </p>
          <button
            onClick={() => navigate('/welcome')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // Clavier à l'écran : fourni par CaisseTactile, comme sur toute la caisse.
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Info vendeur */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
            <span className="text-xl">
              {vendorRole === 'ADMIN' ? '👑' :
               vendorRole === 'MANAGER' ? '⭐' :
               vendorRole === 'CASHIER_PLUS' ? '🎯' : '👤'}
            </span>
            <span className="font-semibold text-gray-800">{vendorName || 'Aucun vendeur'}</span>
          </div>

          {/* Titre */}
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span>📊</span> Gestion des Stocks
          </h1>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={goBack}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all"
          >
            ← Accueil
          </button>
          <button
            onClick={() => navigate('/pos')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
          >
            🛒 POS
          </button>
        </div>
      </header>

      {/* Barre de recherche et filtres */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Recherche */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Rechercher un produit (nom, code, code-barres)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 min-h-[48px] text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filtre catégorie */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-3 min-h-[48px] rounded-full text-base font-medium whitespace-nowrap transition-all ${
                !selectedCategory
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Toutes
            </button>
            {productCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-3 min-h-[48px] rounded-full text-base font-medium whitespace-nowrap transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Rafraîchir — cible tactile : le bouton n'était qu'une icône de 36 px */}
          <button
            onClick={loadData}
            disabled={loading}
            className="px-4 py-3 min-h-[48px] min-w-[48px] text-base font-medium text-gray-700 bg-gray-100 active:bg-gray-200 rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            <span className={loading ? 'animate-spin inline-block' : ''}>🔄</span>
            <span className="hidden sm:inline">Actualiser</span>
          </button>
        </div>
      </div>

      {/* Contenu - Liste des produits groupés par catégorie */}
      <div className="flex-1 p-4 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Chargement...</div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-gray-500">
              <span className="text-4xl block mb-2">📦</span>
              {searchTerm || selectedCategory
                ? 'Aucun produit trouvé avec ces critères'
                : 'Aucun produit dans le catalogue'}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {productsByCategory.map(([categoryName, categoryProducts]) => (
              <div key={categoryName} className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Header catégorie */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                    <span>📁</span>
                    {categoryName}
                    <span className="text-sm font-normal text-gray-500">
                      ({categoryProducts.length} produit{categoryProducts.length > 1 ? 's' : ''})
                    </span>
                  </h2>
                </div>

                {/* Liste des produits */}
                <div className="divide-y divide-gray-100">
                  {categoryProducts.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => openProductDetail(product)}
                      className="p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      {/* Info produit */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-800 truncate">{product.name}</h3>
                        <p className="text-sm text-gray-500">
                          Code: {product.code}
                          {product.barcode && ` • EAN: ${product.barcode}`}
                        </p>
                        <div className="flex gap-4 mt-1 text-sm">
                          <span className="text-orange-600">
                            Réserve: <strong>{formatQty(product.stock_backroom)}</strong> {product.unit}
                          </span>
                          <span className="text-green-600">
                            Rayon: <strong>{formatQty(product.stock_display)}</strong> {product.unit}
                          </span>
                          {product.price_cents && (
                            <span className="text-blue-600">
                              Prix: <strong>{formatPrice(product.price_cents)}</strong>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-3 ml-4">
                        {/* DLC éditable */}
                        {product.primary_lot_id && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <span className="text-xs text-gray-500">DLC:</span>
                            <DLCInput
                              value={product.primary_lot_expiry_date ? product.primary_lot_expiry_date.split('T')[0] : ''}
                              onChange={(isoDate) => handleUpdateDLC(product.id, product.primary_lot_id!, isoDate || null)}
                              compact
                              className="w-44"
                            />
                            {product.primary_lot_expiry_date && new Date(product.primary_lot_expiry_date) < new Date() && (
                              <span className="text-xs text-red-600 font-bold">!</span>
                            )}
                          </div>
                        )}

                        {/* Bouton étiquette */}
                        <button
                          onClick={(e) => openLabelDialog(product, e)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="Imprimer étiquette"
                        >
                          🏷️
                        </button>

                        {/* Mise en rayon si stock en réserve */}
                        {product.stock_backroom > 0 && (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => updateQuantity(product.id, (moveQuantities[product.id] || 1) - 1, e)}
                              className="w-8 h-8 bg-gray-100 rounded-lg text-lg hover:bg-gray-200"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={moveQuantities[product.id] || 0}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateQuantity(product.id, parseInt(e.target.value) || 0);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-14 h-8 text-center border border-gray-300 rounded-lg text-sm"
                            />
                            <button
                              onClick={(e) => updateQuantity(product.id, (moveQuantities[product.id] || 0) + 1, e)}
                              className="w-8 h-8 bg-gray-100 rounded-lg text-lg hover:bg-gray-200"
                            >
                              +
                            </button>
                            <button
                              onClick={(e) => handleMoveToDisplay(product.id, e)}
                              disabled={movingProduct === product.id}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm whitespace-nowrap"
                            >
                              {movingProduct === product.id ? '...' : '→ Rayon'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Fiche Produit (lecture seule) */}
      {showProductDetail && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Fiche Produit</h2>
              <button
                onClick={() => setShowProductDetail(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenu */}
            <div className="px-6 py-4 space-y-4">
              {/* Nom et prix */}
              <div className="text-center border-b pb-4">
                <h3 className="text-2xl font-bold text-gray-800">{selectedProduct.name}</h3>
                {selectedProduct.price_cents && (
                  <p className="text-3xl font-bold text-green-600 mt-2">
                    {formatPrice(selectedProduct.price_cents)}
                  </p>
                )}
              </div>

              {/* Infos générales */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Code</p>
                  <p className="font-semibold text-gray-800">{selectedProduct.code}</p>
                </div>
                {selectedProduct.barcode && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Code-barres</p>
                    <p className="font-semibold text-gray-800">{selectedProduct.barcode}</p>
                  </div>
                )}
                {selectedProduct.category_name && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Catégorie</p>
                    <p className="font-semibold text-gray-800">{selectedProduct.category_name}</p>
                  </div>
                )}
                {selectedProduct.brand && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Marque</p>
                    <p className="font-semibold text-gray-800">{selectedProduct.brand}</p>
                  </div>
                )}
                {selectedProduct.supplier_name && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase">Fournisseur</p>
                    <p className="font-semibold text-gray-800">{selectedProduct.supplier_name}</p>
                  </div>
                )}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase">Unité</p>
                  <p className="font-semibold text-gray-800">{selectedProduct.unit}</p>
                </div>
              </div>

              {/* Stocks */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-600 uppercase">Niveaux de stock</h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setRemiseProduct(selectedProduct)}
                      className="px-3 py-1.5 text-sm font-semibold border-2 border-red-400 text-red-700 rounded-lg active:bg-red-50"
                    >
                      🏷️ Anti-gaspi
                    </button>
                    <button
                      onClick={() => setSeriesProduct(selectedProduct)}
                      className="px-3 py-1.5 text-sm font-semibold border-2 border-slate-400 text-slate-700 rounded-lg active:bg-slate-100"
                    >
                      📊 Séries
                    </button>
                    <button
                      onClick={() => setCountProduct(selectedProduct)}
                      className="px-3 py-1.5 text-sm font-semibold border-2 border-blue-500 text-blue-700 rounded-lg active:bg-blue-50"
                    >
                      🔢 Corriger le stock
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg text-center">
                    <p className="text-xs text-orange-600 uppercase">Réserve</p>
                    <p className="text-3xl font-bold text-orange-700">{formatQty(selectedProduct.stock_backroom)}</p>
                    <p className="text-sm text-orange-600">{selectedProduct.unit}</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-center">
                    <p className="text-xs text-green-600 uppercase">Rayon</p>
                    <p className="text-3xl font-bold text-green-700">{formatQty(selectedProduct.stock_display)}</p>
                    <p className="text-sm text-green-600">{selectedProduct.unit}</p>
                  </div>
                </div>
              </div>

              {/* Lots & Traçabilité AFSCA */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-600 uppercase mb-3">Lots & Traçabilité</h4>
                {lotsLoading ? (
                  <p className="text-gray-500 text-sm text-center py-2">Chargement...</p>
                ) : productLots.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-2">Aucun lot enregistré</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {productLots.map(lot => (
                      <div key={lot.id} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-medium text-gray-800">Lot {lot.lot_number}</span>
                            <span className="text-sm text-gray-500 ml-2">
                              Réception: {new Date(lot.reception_date).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-600">
                              R:{formatQty(lot.stock_backroom)} | D:{formatQty(lot.stock_display)}
                            </span>
                            {lot.source_document_url && (
                              <a
                                href={lot.source_document_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                title="Voir document source (AFSCA)"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </a>
                            )}
                            {lot.label_photo_url && (
                              <a
                                href={lot.label_photo_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-600 hover:text-purple-800"
                                title="Voir photo étiquette"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </a>
                            )}
                          </div>
                        </div>
                        {/* DLC éditable */}
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600">DLC:</label>
                          <DLCInput
                            value={lot.expiry_date ? lot.expiry_date.split('T')[0] : ''}
                            onChange={async (isoDate) => {
                              const newDate = isoDate || null;
                              try {
                                await posApi.updateLot(lot.id, { expiry_date: newDate });
                                setProductLots(prev => prev.map(l =>
                                  l.id === lot.id ? { ...l, expiry_date: newDate } : l
                                ));
                              } catch (err) {
                                console.error('Erreur mise à jour DLC:', err);
                                alert('Erreur lors de la mise à jour de la DLC');
                              }
                            }}
                            compact
                            className="w-48"
                          />
                          {lot.expiry_date && new Date(lot.expiry_date) < new Date() && (
                            <span className="text-xs text-red-600 font-semibold">Expiré!</span>
                          )}
                          {lot.expiry_date && new Date(lot.expiry_date) >= new Date() &&
                           new Date(lot.expiry_date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) && (
                            <span className="text-xs text-orange-600 font-semibold">Expire bientôt</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedProduct.description && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-600 uppercase mb-2">Description</h4>
                  <p className="text-gray-700">{selectedProduct.description}</p>
                </div>
              )}

              {/* Date mise à jour */}
              <div className="text-xs text-gray-400 text-center pt-2">
                Dernière mise à jour: {new Date(selectedProduct.updated_at).toLocaleString('fr-FR')}
              </div>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
              <button
                onClick={() => setShowProductDetail(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  setShowProductDetail(false);
                  openLabelDialog(selectedProduct);
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <span>🏷️</span>
                Imprimer étiquette
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remise anti-gaspi par lot */}
      {remiseProduct && (
        <RemiseLotsModal product={remiseProduct as any} onClose={() => setRemiseProduct(null)} />
      )}

      {seriesProduct && <BilanSeriesModal product={seriesProduct} onClose={() => setSeriesProduct(null)} />}

      {/* Comptage d'un produit (ajustements tracés) */}
      {countProduct && (
        <ProductCountModal
          product={countProduct}
          onClose={() => setCountProduct(null)}
          onSaved={() => {
            loadData();
            if (selectedProduct) {
              posApi.getProductLots(selectedProduct.id).then((all: ProductLot[]) =>
                setProductLots(all || []), // l'endpoint ne renvoie que les lots ACTIVE
              ).catch(() => undefined);
            }
          }}
        />
      )}

      {/* Dialog impression étiquette - utilise les templates du dashboard */}
      {showLabelDialog && productForLabel && (
        <PrintShelfLabelsModal
          onClose={() => {
            setShowLabelDialog(false);
            setProductForLabel(null);
          }}
          preselectedProductId={productForLabel.id}
        />
      )}

    </div>
  );
}
